/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_engine

import io.peekandpoke.klang.common.infra.KlangLock
import io.peekandpoke.klang.common.infra.withLock

/**
 * Announces each structurally-unique [T] to a sink **exactly once**, and hands back the synthetic
 * name it is known by.
 *
 * One generic replacement for what used to be three byte-identical classes (an ignitor, a pipeline
 * and a master registry); they differed only in the DSL type and which
 * `Cmd.Register*` they built. See [InlineDslRegistrar], which owns one instance per DSL kind.
 *
 * **Why announce-once matters:** a top-level `master(…)` re-emits its control event every single
 * cycle, all structurally equal. Without the gate that would be one `RegisterKatalyst` per cycle,
 * forever.
 *
 * Dedup is on the NAME, not on the DSL value. The name already IS structural identity: it comes
 * from the process-wide content-keyed identity maps in `audio_bridge`, so two structurally equal
 * trees have one name by construction and a re-derived-identical tree is recognised as
 * already-announced exactly as before. Keying the set on the value instead would hash the whole
 * tree a second time on every event of every pattern that carries one, which is the cost this
 * class is on the hot path for. A `Set<String>` also keeps the retained memory to one string per
 * unique DSL rather than a reference to the tree.
 *
 * Naming is NOT this class's job: [uniqueId] delegates to those identity maps.
 *
 * @param uniqueId The process-wide synthetic name for a DSL tree. Must be content-derived: this
 *   class trusts it to be equal for equal trees and distinct for distinct ones.
 * @param announce Where a first sighting goes. The live path sends a `Cmd.Register*` over the
 *   wire (the offline renderer too, since KLANG-00399 step 4); an in-process path (a spec that drives
 *   an engine directly) registers straight into a backend registry. Same contract, different destination.
 */
internal class AnnounceOnceRegistry<T : Any>(
    private val uniqueId: (T) -> String,
    private val announce: (name: String, dsl: T) -> Unit,
) {
    private val lock = KlangLock()
    private val announced = mutableSetOf<String>()

    /** Number of unique DSLs announced so far. */
    val size: Int get() = lock.withLock { announced.size }

    /** The synthetic name for [dsl]; announces it on first sighting. */
    fun registerOrLookup(dsl: T): String {
        val name = uniqueId(dsl)
        val firstSighting = lock.withLock { announced.add(name) }

        if (firstSighting) {
            announce(name, dsl)
        }

        return name
    }
}
